import { Tables } from 'src/types/db.types';
import { supabase } from './supabase/client';
import { Creator } from 'src/utils/creator';

export type BehaviourTypeCategory = 'highlight' | 'challenge';

export interface BehaviourType extends Tables<'behaviour_types'> {
  creator?: Creator | null;
}

export interface CreateBehaviourTypeDTO {
  title: string;
  description?: string;
  points?: number;
  type: BehaviourTypeCategory;
  isactive?: boolean;
  extendedproperties?: Record<string, any>;
}

export interface UpdateBehaviourTypeDTO extends Partial<CreateBehaviourTypeDTO> {}

const TABLE_NAME = 'behaviour_types';

export const behaviourService = {
  async getAllBehaviourTypes(type?: BehaviourTypeCategory): Promise<BehaviourType[]> {
    let query = supabase
      .from(TABLE_NAME)
      .select('*, creator:users!behaviour_types_createdby_fkey(id, firstname, lastname, email, teachers(id))')
      .is('deletedat', null)
      .order('createdat', { ascending: false });

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching behavior types: ${error.message}`);
    }

    return data || [];
  },

  async getBehaviourTypeById(id: string): Promise<BehaviourType | null> {
    const { data, error } = await supabase.from(TABLE_NAME).select('*').eq('id', id).is('deletedat', null).single();

    if (error) {
      throw new Error(`Error fetching behavior type: ${error.message}`);
    }

    return data;
  },

  async createBehaviourType(dto: CreateBehaviourTypeDTO): Promise<BehaviourType> {
    if (!dto.points) {
      dto.points = 0;
    }

    // Ensure points are negative for challenges
    const points = dto.type === 'challenge' ? Math.abs(dto.points) * -1 : Math.abs(dto.points);

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert([
        {
          ...dto,
          points,
          isactive: dto.isactive ?? true,
          extendedproperties: dto.extendedproperties ?? {},
        },
      ])
      .select()
      .single();

    if (error) {
      throw new Error(`Error creating behavior type: ${error.message}`);
    }

    return data;
  },

  async updateBehaviourType(id: string, dto: UpdateBehaviourTypeDTO): Promise<BehaviourType> {
    // If points are being updated, ensure they're negative for challenges
    let points = dto.points;
    if (points !== undefined) {
      const { data: existing } = await supabase.from(TABLE_NAME).select('type').eq('id', id).single();

      if (existing?.type === 'challenge') {
        points = Math.abs(points) * -1;
      } else {
        points = Math.abs(points);
      }
    }

    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        ...dto,
        points,
        updatedat: new Date().toISOString(),
      })
      .eq('id', id)
      .is('deletedat', null)
      .select()
      .single();

    if (error) {
      throw new Error(`Error updating behavior type: ${error.message}`);
    }

    return data;
  },

  async deleteBehaviourType(id: string): Promise<void> {
    const { error } = await supabase
      .from(TABLE_NAME)
      .update({
        deletedat: new Date().toISOString(),
        isactive: false,
      })
      .eq('id', id)
      .is('deletedat', null);

    if (error) {
      throw new Error(`Error deleting behavior type: ${error.message}`);
    }
  },
};
